import React, { useState, useEffect, useCallback } from 'react';
import { Banknote, Upload, Trash2, RefreshCw } from 'lucide-react';
import { bankAPI } from '../../lib/api';
import { useTenant } from '../../contexts/TenantContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const NONE = '__none__';

const SIGN_OPTIONS = [
    { key: 'in_positive', label: 'Money in = positive, out = negative (typical)' },
    { key: 'out_positive', label: 'Money out = positive (flip signs)' },
];

function fmtAmount(v) {
    const n = Number(v || 0);
    return n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function BankStatementPage() {
    const { currentTenant } = useTenant();
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [mapping, setMapping] = useState({ date: NONE, text: NONE, amount: NONE, debit: NONE, credit: NONE, currency: NONE, sign_convention: 'in_positive' });
    const [previewing, setPreviewing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [lines, setLines] = useState([]);
    const [fiscalYears, setFiscalYears] = useState([]);
    const [fyFilter, setFyFilter] = useState('all');
    const [loading, setLoading] = useState(false);

    const loadLines = useCallback(async () => {
        if (!currentTenant) return;
        setLoading(true);
        try {
            const fy = fyFilter === 'all' ? null : fyFilter;
            const r = await bankAPI.getLines(currentTenant.id, fy);
            setLines(r.data.lines || []);
            setFiscalYears(r.data.fiscal_years || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not load bank lines');
        } finally { setLoading(false); }
    }, [currentTenant, fyFilter]);

    useEffect(() => { loadLines(); }, [loadLines]);

    const handleFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f || !currentTenant) return;
        setFile(f);
        setPreviewing(true);
        try {
            const r = await bankAPI.preview(currentTenant.id, f);
            setPreview(r.data);
            // Prefer the client's saved mapping, else the server's column suggestion.
            const base = r.data.saved_mapping || r.data.suggested_mapping || {};
            setMapping({
                date: base.date || NONE,
                text: base.text || NONE,
                amount: base.amount || NONE,
                debit: base.debit || NONE,
                credit: base.credit || NONE,
                currency: base.currency || NONE,
                sign_convention: base.sign_convention || 'in_positive',
            });
        } catch (err) {
            setPreview(null);
            toast.error(err.response?.data?.detail || 'Could not read that CSV');
        } finally { setPreviewing(false); }
    };

    const buildMapping = () => {
        const clean = {};
        ['date', 'text', 'amount', 'debit', 'credit', 'currency'].forEach((k) => {
            if (mapping[k] && mapping[k] !== NONE) clean[k] = mapping[k];
        });
        clean.sign_convention = mapping.sign_convention;
        return clean;
    };

    // Mirrors mapping_blockers() in backend/bank_ingest.py. A statement whose description column
    // is unmapped imports rows with empty text that bank matching can never work on, so the import
    // is blocked rather than run silently. Overriding the auto-detected column above clears this.
    const importBlockers = (() => {
        const has = (k) => mapping[k] && mapping[k] !== NONE;
        const out = [];
        if (!has('date')) out.push('Select a date column — bank lines cannot be filed without a date.');
        if (!has('text')) out.push('Select a description column — bank matching needs the text to work.');
        if (!has('amount') && !has('debit') && !has('credit')) {
            out.push('Select an amount column (or debit/credit) — rows without an amount cannot be imported.');
        }
        return out;
    })();

    const handleImport = async () => {
        if (!file || !currentTenant) return;
        const m = buildMapping();
        if (importBlockers.length > 0) {
            toast.error(importBlockers[0]);
            return;
        }
        setImporting(true);
        try {
            const r = await bankAPI.importStatement(currentTenant.id, file, m);
            toast.success(r.data.message || 'Bank lines imported');
            setFile(null); setPreview(null);
            await loadLines();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Import failed');
        } finally { setImporting(false); }
    };

    const handleClear = async () => {
        if (!currentTenant) return;
        const fy = fyFilter === 'all' ? null : fyFilter;
        const scope = fy ? `fiscal year ${fy}` : 'ALL fiscal years';
        if (!window.confirm(`Clear bank lines for ${scope}? This cannot be undone.`)) return;
        try {
            const r = await bankAPI.clearLines(currentTenant.id, fy);
            toast.success(r.data.message || 'Cleared');
            await loadLines();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not clear bank lines');
        }
    };

    const columnSelect = (key, label) => (
        <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">{label}</Label>
            <Select value={mapping[key]} onValueChange={(v) => setMapping((m) => ({ ...m, [key]: v }))}>
                <SelectTrigger className="h-8" data-testid={`map-${key}`}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                    <SelectItem value={NONE}>— none —</SelectItem>
                    {(preview?.columns || []).map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
            </Select>
        </div>
    );

    if (!currentTenant) {
        return (
            <div className="space-y-6" data-testid="bank-page">
                <h1 className="font-heading text-3xl font-bold">Bank statements</h1>
                <Card><CardContent className="py-12 text-center text-muted-foreground">
                    Select a company to import its bank statement.
                </CardContent></Card>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="bank-page">
            <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
                    <Banknote className="h-7 w-7 text-primary" /> Bank statements
                </h1>
                <p className="text-muted-foreground mt-1">
                    Import {currentTenant.name}'s bank statement (CSV). The bank lines are the journal entries —
                    matching and coding come later.
                </p>
            </div>

            {/* Upload + column mapping */}
            <Card>
                <CardHeader><CardTitle className="text-lg">Import a statement</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                        <input id="bank-file" type="file" accept=".csv,text/csv" onChange={handleFile}
                               className="block text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-primary/90"
                               data-testid="bank-file-input" />
                        {previewing && <span className="text-sm text-muted-foreground">Reading…</span>}
                    </div>

                    {preview && (
                        <>
                            <div className="text-xs text-muted-foreground">
                                Detected delimiter <code className="font-mono">{preview.detected?.delimiter}</code>,
                                encoding <code className="font-mono">{preview.detected?.encoding}</code>,
                                {' '}{preview.row_count} row(s).
                                {preview.saved_mapping && <span className="ml-1 text-primary">Saved mapping applied.</span>}
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {columnSelect('date', 'Date column')}
                                {columnSelect('text', 'Description column')}
                                {columnSelect('amount', 'Amount column (signed)')}
                                {columnSelect('debit', 'Debit column (optional)')}
                                {columnSelect('credit', 'Credit column (optional)')}
                                {columnSelect('currency', 'Currency column (optional)')}
                            </div>

                            <div className="space-y-1 max-w-md">
                                <Label className="text-xs text-muted-foreground">Sign convention</Label>
                                <Select value={mapping.sign_convention} onValueChange={(v) => setMapping((m) => ({ ...m, sign_convention: v }))}>
                                    <SelectTrigger className="h-8" data-testid="map-sign"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {SIGN_OPTIONS.map((s) => (<SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Sample rows */}
                            {preview.sample_rows?.length > 0 && (
                                <div className="overflow-x-auto rounded-md border">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>{(preview.columns || []).map((c) => (<TableHead key={c} className="text-xs">{c}</TableHead>))}</TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {preview.sample_rows.map((row, i) => (
                                                <TableRow key={i}>
                                                    {(preview.columns || []).map((c) => (<TableCell key={c} className="text-xs">{row[c]}</TableCell>))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}

                            {importBlockers.length > 0 && (
                                <div className="mb-2 space-y-1" data-testid="bank-import-blockers">
                                    {importBlockers.map((b) => (
                                        <p key={b} className="text-[11px] text-amber-600">{b}</p>
                                    ))}
                                </div>
                            )}
                            <Button onClick={handleImport} disabled={importing || importBlockers.length > 0} data-testid="bank-import-btn">
                                <Upload className="h-4 w-4 mr-2" />
                                {importing ? 'Importing…' : 'Import bank lines'}
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Stored bank lines */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Bank lines ({lines.length})</CardTitle>
                    <div className="flex items-center gap-2">
                        <Select value={fyFilter} onValueChange={setFyFilter}>
                            <SelectTrigger className="h-8 w-36" data-testid="bank-fy-filter"><SelectValue placeholder="Fiscal year" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All fiscal years</SelectItem>
                                {fiscalYears.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                            </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" onClick={loadLines} title="Refresh" data-testid="bank-refresh">
                            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={handleClear} title="Clear bank lines" data-testid="bank-clear">
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {lines.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center">No bank lines yet — import a statement above.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Text</TableHead>
                                        <TableHead className="text-right">Amount</TableHead>
                                        <TableHead>FY</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {lines.map((ln) => (
                                        <TableRow key={ln.id} data-testid={`bank-line-${ln.id}`}>
                                            <TableCell className="font-mono text-sm whitespace-nowrap">{ln.date}</TableCell>
                                            <TableCell className="text-sm">{ln.text}</TableCell>
                                            <TableCell className={`text-right font-mono text-sm ${Number(ln.amount_signed) < 0 ? 'text-destructive' : 'text-success'}`}>
                                                {fmtAmount(ln.amount_signed)}{ln.currency ? ` ${ln.currency}` : ''}
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">{ln.fiscal_year}</TableCell>
                                            <TableCell><Badge variant="secondary">{ln.status}</Badge></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
